/**
 * src/shell/portal/components/MeshTacticsArena.tsx
 *
 * Mesh Tactics Pilot Arcade Arena Component (Step 3.4).
 * Features:
 * - 10x10 Cybernetic Tactical Grid with Cartesian Coordinates (0,0) to (9,9).
 * - 3-Unit Squads (Scout, Heavy, Tech) with directional energy shields & EMP disruption.
 * - Synchronized 10-second planning timer with Action Point budget tracker (5 AP).
 * - Simultaneous Commit-Reveal resolution with live Merkle State Root & RFC 9591 FROST witness attestation.
 */

import React, { useState, useEffect } from 'react';
import { lockstepEngine, LockstepEngine, LockstepMatch } from '../../../domain/arcade/lockstep-engine';
import {
  TacticalUnit,
  UnitAction,
  PlayerActionVector,
  ActionType,
} from '../../../domain/arcade/game-state-contracts';
import { frostWitnessValidator, WitnessNode, AggregatedFrostProof } from '../../../domain/arcade/frost-witness-validator';
import {
  Shield,
  Zap,
  Crosshair,
  Move,
  Clock,
  Award,
  Lock,
  Unlock,
  CheckCircle2,
  X,
  Swords,
  ChevronRight,
  RotateCcw,
} from 'lucide-react';

export interface MeshTacticsArenaProps {
  isOpen: boolean;
  onClose: () => void;
  playerDid?: string;
  opponentDid?: string;
}

export const MeshTacticsArena: React.FC<MeshTacticsArenaProps> = ({
  isOpen,
  onClose,
  playerDid = 'did:key:tactician_alice',
  opponentDid = 'did:key:tactician_bob',
}) => {
  const matchId = 'tactics_arena_match_01';
  const [match, setMatch] = useState<LockstepMatch | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [selectedActionType, setSelectedActionType] = useState<ActionType>('MOVE');
  const [targetCoord, setTargetCoord] = useState<{ x: number; y: number } | null>(null);
  const [stagedActions, setStagedActions] = useState<UnitAction[]>([]);
  const [secretSalt, setSecretSalt] = useState<string>('');
  const [hasCommitted, setHasCommitted] = useState<boolean>(false);
  const [hasRevealed, setHasRevealed] = useState<boolean>(false);
  const [eventLog, setEventLog] = useState<string[]>([]);
  const [frostProof, setFrostProof] = useState<AggregatedFrostProof | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(10);

  // Initialize match
  useEffect(() => {
    if (!isOpen) return;

    let current = lockstepEngine.getMatch(matchId);
    if (!current) {
      current = lockstepEngine.createMatch({
        matchId,
        player1Did: playerDid,
        player2Did: opponentDid,
      });
    }

    setMatch(current);
    setSecretSalt(Math.random().toString(36).slice(2) + Date.now().toString(36));
    setStagedActions([]);
    setHasCommitted(false);
    setHasRevealed(false);
    setEventLog([`Match initiated. Tick #1 planning phase open.`]);

    // Attest initial state with mock FROST witnesses
    attestFrostState(current.tickNumber, current.stateRoot);
  }, [isOpen, playerDid, opponentDid]);

  // Turn timer countdown
  useEffect(() => {
    if (!isOpen || !match || match.isGameOver) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => (prev > 0 ? prev - 1 : 10));
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, match]);

  if (!isOpen || !match) return null;

  const totalApSpent = stagedActions.reduce((acc, a) => acc + a.costAp, 0);
  const remainingAp = LockstepEngine.MAX_AP_PER_TURN - totalApSpent;

  const attestFrostState = (tickNumber: number, stateRoot: string) => {
    try {
      const candidateSentinels: WitnessNode[] = Array.from({ length: 5 }).map((_, i) => ({
        did: `did:key:witness_sentinel_${i + 1}`,
        publicKeyHex: `02abcdef01234567890abcdef1234567890abcdef1234567890abcdef12345678${i}`,
        sovereignScore: 650 + i * 20,
      }));

      const witnesses = frostWitnessValidator.sampleWitnessPool(matchId, tickNumber, candidateSentinels, 5);
      const shares = witnesses.slice(0, 3).map((w, i) =>
        frostWitnessValidator.generatePartialSignatureShare({
          witness: w,
          privateKeySeed: `seed_${i}`,
          matchId,
          tickNumber,
          stateRoot,
        })
      );

      const proof = frostWitnessValidator.aggregateWitnessSignatures({
        shares,
        groupPublicKeyHex: '04a1b2c3d4e5f67890abcdef1234567890abcdef1234567890abcdef1234567890',
        totalWitnesses: 5,
      });

      setFrostProof(proof);
    } catch {}
  };

  const handleStageAction = () => {
    if (!selectedUnitId) return;
    const unit = match.units[selectedUnitId];
    if (!unit) return;

    let costAp = 2;
    let initiative = 50;

    switch (selectedActionType) {
      case 'HACK_EMP':
        costAp = 3;
        initiative = 100;
        break;
      case 'DEPLOY_SHIELD':
        costAp = 2;
        initiative = 80;
        break;
      case 'MOVE':
        costAp = 2;
        initiative = 50;
        break;
      case 'FIRE':
        costAp = 3;
        initiative = 30;
        break;
    }

    if (totalApSpent + costAp > LockstepEngine.MAX_AP_PER_TURN) {
      alert(`Insufficient AP! Action requires ${costAp} AP, you have ${remainingAp} remaining.`);
      return;
    }

    const action: UnitAction = {
      unitId: selectedUnitId,
      type: selectedActionType,
      targetCoord: targetCoord || undefined,
      costAp,
      initiative,
    };

    setStagedActions((prev) => [...prev, action]);
  };

  const handleCommitOrders = () => {
    const vectorP1: PlayerActionVector = {
      playerDid,
      tickNumber: match.tickNumber,
      actions: stagedActions,
      totalApSpent,
    };

    const commit1 = LockstepEngine.computeCommitment(vectorP1, secretSalt, matchId);
    lockstepEngine.submitCommitment(matchId, playerDid, commit1);

    // Simulate AI opponent committing
    const p2Scout = Object.values(match.units).find((u) => u.ownerDid === opponentDid && u.type === 'SCOUT');
    const p2Actions: UnitAction[] = p2Scout
      ? [
          {
            unitId: p2Scout.id,
            type: 'MOVE',
            targetCoord: { x: Math.max(0, p2Scout.coord.x - 1), y: p2Scout.coord.y },
            costAp: 2,
            initiative: 50,
          },
        ]
      : [];

    const vectorP2: PlayerActionVector = {
      playerDid: opponentDid,
      tickNumber: match.tickNumber,
      actions: p2Actions,
      totalApSpent: 2,
    };
    const commit2 = LockstepEngine.computeCommitment(vectorP2, 'salt_p2', matchId);
    lockstepEngine.submitCommitment(matchId, opponentDid, commit2);

    setHasCommitted(true);
    setEventLog((prev) => [
      `Turn #${match.tickNumber} orders encrypted & committed to match ledger.`,
      ...prev,
    ]);

    // Automatically trigger simultaneous reveal
    setTimeout(() => {
      handleSimultaneousReveal(vectorP1, vectorP2);
    }, 1200);
  };

  const handleSimultaneousReveal = (vectorP1: PlayerActionVector, vectorP2: PlayerActionVector) => {
    lockstepEngine.submitReveal(matchId, playerDid, vectorP1, secretSalt);
    lockstepEngine.submitReveal(matchId, opponentDid, vectorP2, 'salt_p2');

    const result = lockstepEngine.resolveTick(matchId);
    setMatch({ ...lockstepEngine.getMatch(matchId)! });
    setHasCommitted(false);
    setHasRevealed(false);
    setStagedActions([]);
    setSecretSalt(Math.random().toString(36).slice(2) + Date.now().toString(36));
    setTimeRemaining(10);

    setEventLog((prev) => [...result.events, `--- Tick #${result.state.tickNumber} Commenced ---`, ...prev]);
    attestFrostState(result.state.tickNumber, result.state.stateRoot);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(5, 8, 15, 0.92)',
        backdropFilter: 'blur(16px)',
        padding: 20,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div
        style={{
          background: '#0a0e1a',
          border: '1px solid #1e293b',
          borderRadius: 20,
          maxWidth: 1040,
          width: '100%',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 25px 60px rgba(0,0,0,0.8)',
          color: '#f8fafc',
        }}
      >
        {/* Top Header */}
        <div
          style={{
            padding: '16px 24px',
            background: '#101726',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'rgba(56, 189, 248, 0.15)',
                border: '1px solid #38bdf8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Swords size={20} color="#38bdf8" />
            </div>
            <div>
              <div style={{ fontWeight: 900, fontSize: 16 }}>Mesh Tactics: Sovereign Arcade</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                Tick #{match.tickNumber} &bull; Simultaneous Lockstep &bull; RFC 9591 FROST Quorum
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* FROST Attestation Pill */}
            {frostProof && (
              <div
                style={{
                  fontSize: 11,
                  padding: '4px 10px',
                  borderRadius: 8,
                  background: 'rgba(16, 185, 129, 0.15)',
                  border: '1px solid #10b981',
                  color: '#34d399',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontWeight: 700,
                }}
              >
                <CheckCircle2 size={13} />
                FROST Verified ({frostProof.thresholdQuorum}/{frostProof.totalWitnesses})
              </div>
            )}

            {/* Countdown timer */}
            <div
              style={{
                fontSize: 12,
                padding: '4px 10px',
                borderRadius: 8,
                background: timeRemaining <= 3 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(56, 189, 248, 0.15)',
                border: `1px solid ${timeRemaining <= 3 ? '#ef4444' : '#38bdf8'}`,
                color: timeRemaining <= 3 ? '#fca5a5' : '#38bdf8',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontWeight: 800,
              }}
            >
              <Clock size={13} /> {timeRemaining}s
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                cursor: 'pointer',
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Main Body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Column: 10x10 Grid */}
          <div
            style={{
              flex: 1,
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#070a12',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(10, 42px)',
                gridTemplateRows: 'repeat(10, 42px)',
                gap: 3,
                background: '#101626',
                padding: 10,
                borderRadius: 14,
                border: '1px solid #1e293b',
              }}
            >
              {Array.from({ length: 10 }).map((_, y) =>
                Array.from({ length: 10 }).map((_, x) => {
                  const unit = Object.values(match.units).find((u) => u.coord.x === x && u.coord.y === y);
                  const isSelectedTarget = targetCoord?.x === x && targetCoord?.y === y;
                  const isFriendly = unit?.ownerDid === playerDid;

                  return (
                    <div
                      key={`${x}-${y}`}
                      onClick={() => {
                        if (unit && unit.ownerDid === playerDid) {
                          setSelectedUnitId(unit.id);
                        } else {
                          setTargetCoord({ x, y });
                        }
                      }}
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: 6,
                        background: isSelectedTarget
                          ? 'rgba(239, 68, 68, 0.3)'
                          : selectedUnitId && match.units[selectedUnitId]?.coord.x === x && match.units[selectedUnitId]?.coord.y === y
                          ? 'rgba(56, 189, 248, 0.25)'
                          : '#0a0f1d',
                        border: isSelectedTarget
                          ? '1px solid #ef4444'
                          : selectedUnitId && match.units[selectedUnitId]?.coord.x === x && match.units[selectedUnitId]?.coord.y === y
                          ? '1px solid #38bdf8'
                          : '1px solid #161f33',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        position: 'relative',
                        fontSize: 10,
                      }}
                    >
                      {unit && (
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 6,
                            background: isFriendly ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)',
                            color: '#ffffff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 900,
                            fontSize: 9,
                            position: 'relative',
                            boxShadow: unit.isShielded ? '0 0 10px #38bdf8' : 'none',
                          }}
                        >
                          {unit.type === 'SCOUT' && 'SCOUT'}
                          {unit.type === 'HEAVY' && 'HEAVY'}
                          {unit.type === 'TECH' && 'TECH'}
                          <span style={{ fontSize: 8, opacity: 0.85 }}>{unit.hp}HP</span>

                          {unit.isShielded && (
                            <Shield size={10} color="#38bdf8" style={{ position: 'absolute', top: -3, right: -3 }} />
                          )}
                          {unit.isEmpDisabled && (
                            <Zap size={10} color="#f59e0b" style={{ position: 'absolute', top: -3, left: -3 }} />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* State Root HUD */}
            <div
              style={{
                marginTop: 14,
                fontSize: 11,
                color: '#64748b',
                fontFamily: 'monospace',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span>Merkle State Root:</span>
              <span style={{ color: '#38bdf8' }}>{match.stateRoot.slice(0, 24)}...</span>
            </div>
          </div>

          {/* Right Column: Actions & Event Log */}
          <div
            style={{
              width: 380,
              borderLeft: '1px solid #1e293b',
              background: '#0d1322',
              display: 'flex',
              flexDirection: 'column',
              padding: 18,
              gap: 16,
              overflowY: 'auto',
            }}
          >
            {/* Squad Status */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', marginBottom: 8 }}>
                Your Squad ({playerDid.slice(0, 12)})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {Object.values(match.units)
                  .filter((u) => u.ownerDid === playerDid)
                  .map((unit) => (
                    <div
                      key={unit.id}
                      onClick={() => setSelectedUnitId(unit.id)}
                      style={{
                        padding: '8px 12px',
                        background: selectedUnitId === unit.id ? 'rgba(56, 189, 248, 0.15)' : '#070a12',
                        border: selectedUnitId === unit.id ? '1px solid #38bdf8' : '1px solid #1e293b',
                        borderRadius: 8,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ fontWeight: 700 }}>
                        {unit.type} at ({unit.coord.x}, {unit.coord.y})
                      </span>
                      <span style={{ color: '#34d399', fontWeight: 600 }}>{unit.hp} / {unit.maxHp} HP</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Action Selection */}
            <div style={{ background: '#111726', padding: 14, borderRadius: 12, border: '1px solid #1e293b' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 12 }}>
                <span style={{ fontWeight: 700 }}>Action Selection</span>
                <span style={{ color: '#38bdf8', fontWeight: 800 }}>{remainingAp} AP Left</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={() => setSelectedActionType('MOVE')}
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    border: '1px solid',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: selectedActionType === 'MOVE' ? 'rgba(56, 189, 248, 0.2)' : '#070a12',
                    borderColor: selectedActionType === 'MOVE' ? '#38bdf8' : '#1e293b',
                    color: selectedActionType === 'MOVE' ? '#38bdf8' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                  }}
                >
                  <Move size={12} /> Move (2 AP)
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedActionType('FIRE')}
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    border: '1px solid',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: selectedActionType === 'FIRE' ? 'rgba(239, 68, 68, 0.2)' : '#070a12',
                    borderColor: selectedActionType === 'FIRE' ? '#ef4444' : '#1e293b',
                    color: selectedActionType === 'FIRE' ? '#ef4444' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                  }}
                >
                  <Crosshair size={12} /> Fire (3 AP)
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedActionType('DEPLOY_SHIELD')}
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    border: '1px solid',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: selectedActionType === 'DEPLOY_SHIELD' ? 'rgba(16, 185, 129, 0.2)' : '#070a12',
                    borderColor: selectedActionType === 'DEPLOY_SHIELD' ? '#10b981' : '#1e293b',
                    color: selectedActionType === 'DEPLOY_SHIELD' ? '#10b981' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                  }}
                >
                  <Shield size={12} /> Shield (2 AP)
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedActionType('HACK_EMP')}
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    border: '1px solid',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: selectedActionType === 'HACK_EMP' ? 'rgba(245, 158, 11, 0.2)' : '#070a12',
                    borderColor: selectedActionType === 'HACK_EMP' ? '#f59e0b' : '#1e293b',
                    color: selectedActionType === 'HACK_EMP' ? '#f59e0b' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                  }}
                >
                  <Zap size={12} /> EMP (3 AP)
                </button>
              </div>

              <button
                type="button"
                onClick={handleStageAction}
                disabled={!selectedUnitId}
                style={{
                  width: '100%',
                  padding: 8,
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  color: '#ffffff',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginBottom: 8,
                }}
              >
                + Queue Unit Order ({stagedActions.length} queued)
              </button>

              <button
                type="button"
                onClick={handleCommitOrders}
                disabled={hasCommitted || match.isGameOver}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: hasCommitted ? 'rgba(56, 189, 248, 0.2)' : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  border: 'none',
                  borderRadius: 8,
                  color: '#ffffff',
                  fontWeight: 800,
                  fontSize: 12,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                {hasCommitted ? (
                  <>
                    <Lock size={14} /> Orders Encrypted & Locked...
                  </>
                ) : (
                  <>
                    <Lock size={14} /> Commit Simultaneous Turn
                  </>
                )}
              </button>
            </div>

            {/* Combat Event Log */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>
                Tactical Event Replay
              </div>
              <div
                style={{
                  flex: 1,
                  background: '#070a12',
                  border: '1px solid #1e293b',
                  borderRadius: 8,
                  padding: 10,
                  fontSize: 11,
                  fontFamily: 'monospace',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  maxHeight: 180,
                }}
              >
                {eventLog.map((log, idx) => (
                  <div key={idx} style={{ color: log.startsWith('---') ? '#38bdf8' : '#94a3b8' }}>
                    {log}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
